import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { CheckCircle2, XCircle } from "lucide-react";

export function DonateSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState({ status: "polling", info: null, attempts: 0 });

  useEffect(() => {
    if (!sessionId) { setState({ status: "error", info: null, attempts: 0 }); return; }
    let cancelled = false;
    const poll = async (attempt) => {
      if (cancelled) return;
      try {
        const { data } = await http.get(`/donations/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setState({ status: "paid", info: data, attempts: attempt });
        } else if (attempt < 12) {
          setTimeout(() => poll(attempt + 1), 2000);
          setState((s) => ({ ...s, attempts: attempt + 1 }));
        } else {
          setState({ status: "timeout", info: data, attempts: attempt });
        }
      } catch {
        setState({ status: "error", info: null, attempts: attempt });
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div data-testid="donate-success" className="max-w-[560px] mx-auto px-6 py-24 text-center">
      {state.status === "paid" ? (
        <>
          <CheckCircle2 className="w-16 h-16 mx-auto" style={{ color: "var(--wofy-primary)" }} />
          <h1 className="font-display text-4xl mt-6">Thank you for your gift.</h1>
          <p className="mt-4 text-base" style={{ color: "var(--wofy-text-soft)" }}>
            Your donation of <b style={{ color: "var(--wofy-text)" }}>${state.info.amount?.toFixed(2)} {state.info.currency?.toUpperCase()}</b> has been received.
            A receipt from Stripe is on its way to your inbox.
          </p>
          <div className="mt-8 flex gap-3 justify-center">
            <Link to="/" className="wofy-btn-primary">Return home</Link>
            <Link to="/donate" className="wofy-btn-ghost">Donate again</Link>
          </div>
        </>
      ) : state.status === "timeout" ? (
        <>
          <h1 className="font-display text-3xl">Almost there…</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            Your payment is being confirmed by Stripe. Please check your email in a minute or two.
          </p>
          <Link to="/" className="wofy-btn-primary mt-6">Return home</Link>
        </>
      ) : state.status === "error" ? (
        <>
          <XCircle className="w-16 h-16 mx-auto" style={{ color: "#B23A3A" }} />
          <h1 className="font-display text-3xl mt-6">We couldn't verify your payment.</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>Please contact us if you were charged.</p>
          <Link to="/contact" className="wofy-btn-primary mt-6">Contact support</Link>
        </>
      ) : (
        <>
          <div className="w-8 h-8 mx-auto border-2 rounded-full animate-spin" style={{ borderColor: "var(--wofy-border)", borderTopColor: "var(--wofy-primary)" }} />
          <h1 className="font-display text-3xl mt-6">Confirming your donation…</h1>
          <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>This usually takes a couple of seconds.</p>
        </>
      )}
    </div>
  );
}

export function DonateCancel() {
  return (
    <div data-testid="donate-cancel" className="max-w-[560px] mx-auto px-6 py-24 text-center">
      <XCircle className="w-16 h-16 mx-auto" style={{ color: "var(--wofy-text-soft)" }} />
      <h1 className="font-display text-3xl mt-6">Checkout cancelled</h1>
      <p className="mt-3 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
        No charge was made. You can try again whenever you're ready.
      </p>
      <div className="mt-8 flex gap-3 justify-center">
        <Link to="/donate" className="wofy-btn-primary">Try again</Link>
        <Link to="/" className="wofy-btn-ghost">Return home</Link>
      </div>
    </div>
  );
}
